'use client'

// 903-б: подписи — из словаря каталога (catalog.safelogist).
import { useTranslations } from 'next-intl'
// ============================================================================
// ОКНО ПРИЗЫВА ИЗ БАННЕРА.
// ----------------------------------------------------------------------------
// Баннер висит между строками ленты и НЕ знает, о какой фирме речь. Поэтому
// окно здесь другое, чем на странице фирмы: там оно про конкретную компанию и
// умеет запустить проверку, здесь — про саму услугу.
//
// ── ПОЧЕМУ НЕ УВОДИМ ССЫЛКОЙ ───────────────────────────────────────────────
// Раньше кнопка вела на `safelogist.net` — клиент уходил к партнёру и не
// возвращался, хотя проверку продаём мы. Уводить в «Репутацию» тоже плохо:
// человек читал ленту, и выдёргивать его со страницы ради объяснения незачем.
//
// Окно объясняет на месте и возвращает к чтению одним нажатием.
//
// ── ТРИ СОСТОЯНИЯ, И В КАЖДОМ РАЗГОВОР СВОЙ ────────────────────────────────
// Гость — «что это даёт и почему нужен вход».
// Вошедший с остатком — «проверять можно прямо сейчас, вот где».
// Вошедший без остатка — «бесплатное остаётся, реестры платные».
//
// Одна общая надпись на все случаи заставила бы каждого догадываться, про него
// она или нет.
// ============================================================================

import { useState } from 'react'
import { ShieldCheck } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'

export interface BannerCheckState {
  signedIn: boolean
  /** Остаток проверок. У гостя всегда ноль — он его и не видит. */
  balance: number
  /** Право запускать проверку. Без него человек видит, но не нажимает. */
  canCheck: boolean
}

export function BannerCheckModal({
  label, state,
}: {
  label: string
  state: BannerCheckState
}) {
  const t = useTranslations('catalog.safelogist')
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="shrink-0 rounded-xl bg-accent-600 px-5 py-2.5 text-[13px] font-bold text-white"
      >
        {label}
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        size="md"
        title={
          <span className="flex items-center gap-2">
            <ShieldCheck size={19} className="text-accent-600" />
            <span>{t('bannerTitle')}</span>
          </span>
        }
      >
        {/* Состав — один на все состояния: человек должен понимать, за что
            платит, ещё до того, как решит платить. */}
        <div className="space-y-2">
          <Fact
            title={t('bVerdictTitle')}
            text={t('bVerdictText')}
          />
          <Fact
            title={t('bTermsTitle')}
            text={t('bTermsText')}
          />
          <Fact
            title={t('bCourtsTitle')}
            text={t('bCourtsText')}
          />
          <Fact
            title={t('bReviewsTitle')}
            text={t('bReviewsText')}
          />
        </div>

        {!state.signedIn ? (
          <Pane tone="acc" title={t('bGuestTitle')}>
            <p>
              {t('bGuestText')}
            </p>
            <p className="mt-1.5">
              {t.rich('bFreeChecks', { b: chunks => <b>{chunks}</b> })}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href="/auth/signup"
                className="rounded-xl bg-accent-600 px-4 py-2.5 text-[13px] font-bold text-white"
              >
                {t('createAccount')}
              </a>
              <a
                href="/auth/login?next=/reputation"
                className="rounded-xl border-hairline border-ink-300 px-4 py-2.5 text-[13px] font-semibold text-ink-700"
              >
                {t('signIn')}
              </a>
            </div>
          </Pane>
        ) : !state.canCheck ? (
          <Pane tone="amber" title={t('noRightTitle')}>
            <p>
              {t('noRightText')}
            </p>
          </Pane>
        ) : state.balance < 1 ? (
          <Pane tone="amber" title={t('noChecksTitle')}>
            <p>
              {t('bNoChecksText')}
            </p>
            <a
              href="/reputation"
              className="mt-3 inline-block rounded-xl bg-ink-900 px-4 py-2.5 text-[13px] font-bold text-white"
            >
              {t('buyChecks')}
            </a>
          </Pane>
        ) : (
          <Pane tone="mint" title={t('bBalance', { n: state.balance })}>
            <p>
              {t('bBalanceText')}
            </p>
            <a
              href="/reputation"
              className="mt-3 inline-block rounded-xl bg-accent-600 px-4 py-2.5 text-[13px] font-bold text-white"
            >
              {t('bGoReputation')}
            </a>
          </Pane>
        )}
      </Modal>
    </>
  )
}

function Fact({ title, text }: { title: string; text: string }) {
  return (
    <div className="flex gap-2.5 rounded-xl border-hairline border-ink-150 px-3 py-2.5">
      <span className="font-bold text-status-green-text">✓</span>
      <div>
        <div className="text-[12.8px] font-semibold text-ink-900">{title}</div>
        <div className="mt-0.5 text-[12.3px] text-ink-600">{text}</div>
      </div>
    </div>
  )
}

function Pane({
  tone, title, children,
}: { tone: 'acc' | 'amber' | 'mint'; title: string; children: React.ReactNode }) {
  const cls = tone === 'amber'
    ? 'bg-status-amber-bg'
    : tone === 'mint' ? 'bg-status-green-bg' : 'bg-accent-50'
  return (
    <div className={'mt-4 rounded-xl px-4 py-3.5 ' + cls}>
      <div className="text-[13.5px] font-bold text-ink-900">{title}</div>
      <div className="mt-1 text-[12.5px] text-ink-700">{children}</div>
    </div>
  )
}

